import { RoutesE } from "../../../routes/routesData";

/**
 * Preserving a guest's intended destination across the login page.
 *
 * A signed-out visitor who opens a deep link (e.g. a shared /searchresults link with
 * filters) hits the publicRoutes catch-all, because private routes are not registered
 * without a user. That catch-all sends them to the login page; these helpers carry the
 * destination along as a `returnUrl` query parameter so `useAccount.login()` can return
 * them to it instead of to their role's default landing page.
 *
 * The parameter is deliberately in the URL rather than in router state or storage: it
 * then survives a refresh of the login page, a failed login attempt, and a detour to
 * Forgot Password.
 */

export const RETURN_URL_PARAM = "returnUrl";

/** The parts of a react-router `Location` these helpers need. */
export type ReturnUrlLocationT = {
  pathname: string;
  search: string;
  hash: string;
};

/**
 * The app root is where role-based redirects already send people, and /account/* pages
 * are the login flow itself — preserving either would at best be a no-op and at worst
 * bounce the user back to login after signing in.
 */
const isPreservableDestination = (pathname: string): boolean =>
  pathname !== "/" && !pathname.startsWith("/account/");

/**
 * Builds the login URL a guest should be redirected to, preserving where they were
 * headed. Falls back to the bare login URL when the destination is not worth keeping.
 */
export const buildLoginUrlWithReturn = (
  location: ReturnUrlLocationT
): string => {
  if (!isPreservableDestination(location.pathname)) {
    return RoutesE.LOGIN;
  }

  const destination = `${location.pathname}${location.search}${location.hash}`;

  return `${RoutesE.LOGIN}?${RETURN_URL_PARAM}=${encodeURIComponent(
    destination
  )}`;
};

/**
 * Reads and validates the preserved destination out of the login page's query string.
 * Returns a path safe to hand to react-router's `navigate()`, or null to fall back to
 * the role-based redirect.
 *
 * Security: this value is attacker-controllable (anyone can hand-craft a login link), so
 * it must never be able to point off-origin. `new URL(...)` is used purely as a
 * validator — resolving the candidate against our own origin and demanding the result
 * still be our origin rejects protocol-relative (//evil.com), absolute
 * (https://evil.com), backslash-smuggled (/\evil.com, which WHATWG normalises to //) and
 * scheme-based (javascript:) values under one rule instead of a prefix blacklist.
 *
 * The accepted value is the decoded string itself, NOT a rebuild from url.pathname +
 * url.search: rebuilding normalises legal query characters (filterLabel=Brand%3A+... comes
 * back as filterLabel=Brand:+...), and the destination should reach the page exactly as
 * the user originally opened it. Note that URLSearchParams.get() already percent-decodes
 * once — decoding again here would both corrupt such a query and let a double-encoded
 * value slip past the checks below.
 */
export const getSafeReturnUrl = (search: string): string | null => {
  const destination = new URLSearchParams(search).get(RETURN_URL_PARAM);

  if (!destination || !destination.startsWith("/")) {
    return null;
  }

  let url: URL;

  try {
    url = new URL(destination, window.location.origin);
  } catch {
    return null;
  }

  if (url.origin !== window.location.origin) {
    return null;
  }

  if (!isPreservableDestination(url.pathname)) {
    return null;
  }

  return destination;
};
